import { FILER_KINDS, RETURN_FORMS } from '@oneapp/gst';
import { z } from 'zod';
import { SupportContactSchema } from './console';
import { PrivacyContactSchema } from './privacy';

export const APP_ENVS = ['local', 'test', 'ci', 'staging', 'production'] as const;
export const AppEnvSchema = z.enum(APP_ENVS);
export type AppEnv = z.infer<typeof AppEnvSchema>;

/** GET /api/v1/meta */
export const MetaResponseSchema = z.object({
  name: z.literal('oneapp-api'),
  version: z.string(),
  commit: z.string().nullable(),
  env: AppEnvSchema,
  time: z.string(),
  flags: z.record(z.string(), z.boolean()),
  /** Who runs EkApp and its Grievance Officer (Phase 17), for the privacy notice and Privacy and consents. */
  privacy: PrivacyContactSchema,
  /** Support's email, phone and hours (gap 7: Help), each null until set. */
  support: SupportContactSchema.default({ email: null, phone: null, hours: null }),
});
export type MetaResponse = z.infer<typeof MetaResponseSchema>;

/**
 * pass — the key works against its service
 * fail — set but not working (or required and missing)
 * warn — works, but something should change (e.g. test keys in production)
 * skip — not set yet, and not needed until a later phase
 */
export const DoctorStatusSchema = z.enum(['pass', 'fail', 'warn', 'skip']);
export type DoctorStatus = z.infer<typeof DoctorStatusSchema>;

export const DoctorCheckSchema = z.object({
  id: z.string(),
  group: z.string(),
  label: z.string(),
  status: DoctorStatusSchema,
  /** What happened, in plain words. Never contains a key value. */
  detail: z.string(),
  /** Keys this check covers, e.g. ["OPENAI_API_KEY"]. */
  keys: z.array(z.string()),
  /** Phase that first needs it, e.g. "P1". */
  neededFrom: z.string().nullable(),
  ms: z.number().int().nonnegative(),
});
export type DoctorCheck = z.infer<typeof DoctorCheckSchema>;

/** GET /api/v1/admin/doctor */
export const DoctorReportSchema = z.object({
  env: AppEnvSchema,
  version: z.string(),
  ranAt: z.string(),
  ok: z.boolean(),
  counts: z.object({ pass: z.number().int(), fail: z.number().int(), warn: z.number().int(), skip: z.number().int() }),
  checks: z.array(DoctorCheckSchema),
});
export type DoctorReport = z.infer<typeof DoctorReportSchema>;

/** POST /api/v1/admin/test-error */
export const TestErrorResponseSchema = z.object({
  sent: z.boolean(),
  eventId: z.string().nullable(),
  detail: z.string(),
});
export type TestErrorResponse = z.infer<typeof TestErrorResponseSchema>;

// ---------------------------------------------------------------------------
// Due-date changes (Phase 5): government extensions, added without a release.
//
//   GET    /api/v1/admin/due-dates?all=true      -> DueDateOverrideList (active ones; all with withdrawn)
//   POST   /api/v1/admin/due-dates               AddDueDateOverride -> DueDateOverrideResponse (201)
//   DELETE /api/v1/admin/due-dates/:id           -> DueDateOverrideResponse (withdrawn)
// ---------------------------------------------------------------------------

export const AddDueDateOverrideSchema = z.strictObject({
  form: z.enum(RETURN_FORMS),
  /** The period's last month, YYYY-MM (a quarter's last month for quarterly returns, March for GSTR-4). */
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { error: 'Use YYYY-MM' }),
  /** Only this kind of filer; leave out for everyone who files the form. */
  filer: z.enum(FILER_KINDS).optional(),
  /** Only these states (GSTIN state codes, e.g. "29"); leave out for all. */
  stateCodes: z
    .array(z.string().regex(/^\d{2}$/))
    .min(1)
    .max(40)
    .optional(),
  /** The new due date, YYYY-MM-DD. */
  dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Use YYYY-MM-DD' }),
  /** Why, in a few words. Optional: the console (X6) records the notification instead. */
  reason: z.string().trim().min(3).max(200).optional(),
  /** Where it's from, e.g. "Notification 01/2025-Central Tax". */
  reference: z.string().trim().min(3).max(200).optional(),
});
export type AddDueDateOverride = z.infer<typeof AddDueDateOverrideSchema>;

export const DueDateOverrideSchema = z.object({
  id: z.string(),
  form: z.enum(RETURN_FORMS),
  period: z.string(),
  filer: z.enum(FILER_KINDS).nullable(),
  stateCodes: z.array(z.string()).nullable(),
  dueOn: z.string(),
  reason: z.string().nullable(),
  reference: z.string().nullable(),
  createdAt: z.string(),
  /** The admin's account id, or "admin_token". */
  createdBy: z.string(),
  /** The admin's name, for "by Anita, 2 Oct" (null with the admin token, or once their account is gone). */
  createdByName: z.string().nullable().default(null),
  withdrawnAt: z.string().nullable(),
  withdrawnBy: z.string().nullable(),
});
export type DueDateOverrideEntry = z.infer<typeof DueDateOverrideSchema>;

export const DueDateOverrideListSchema = z.object({ overrides: z.array(DueDateOverrideSchema) });
export const DueDateOverrideResponseSchema = z.object({ override: DueDateOverrideSchema });
